/* ===================================================================
   ConfirmBusinessCard -- the disambiguation + confirm surface (REBUILD,
   2026-07-07, onboarding redesign BUILD-SPEC section 13.1).

   THE most important screen in onboarding: a wrong match here IS the
   wrong-listing bug (customers get sent to a competitor's Google page).
   So it is built mobile-first, with the FULL street address as the primary
   disambiguator and an EXPLICIT affirmative -- never an implicit "Next".

   FLOW (spec sections 3.1 - 3.4):
     - Prefer the AUTHENTICATED listing. The backend returns it as
       google_candidate (status === 'listing_pending_confirmation'); we
       auto-select it and lead with it, NOT the free-text search.
     - The owner must take a clearly labelled "Yes, this is my business"
       action. An equally reachable "This isn't my business" drops to the
       manual-connect screen (GBP managed-listings picker + pasted review
       link) so a mismatched owner never feels funneled into confirming.
       The Places name-search box is REMOVED (2026-07-16, Steve): it returns
       ZERO_RESULTS for a service-area business, permanently.
     - Confirm -> POST /integrations/google/confirm-listing. The backend
       VALIDATES (place resolves + >= 1 review) before saving, so a wrong
       Places pick is blocked server-side (422) and nothing is saved.
     - Success reflects the owner's real data back (rating + newest review
       from the CONFIRMED listing) -- the dopamine hit AND a human-in-the-
       loop guardrail: someone else's reviews jump out instantly.

   Canonical connected-state: SENDING is gated on
   google_connection_status === 'connected' (read by the parent), NOT on this
   card's local flags. Tokens only, no hardcoded hex. Nothing auto-posts.
   =================================================================== */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useBusiness, useConfirmListing } from '../hooks';
import { api, ApiError } from '../lib/api';
import type {
  ConfirmListingResult,
  ConfirmedListing,
  ConnectReviewLinkResult,
  GbpManagedLocation,
} from '../lib/api';
import { parseReviewLink } from '../lib/googleReviewLink';
import { apiErrorMessage } from '../lib/display';

/* ---------------- inline glyphs ---------------- */
function CheckIcon({ size = 16 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} width={size} height={size} aria-hidden="true">
      <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* Gold star + rating + review count (spec: gold star per brand mark).

   NULL-SAFE BY CONTRACT. A service-area / hidden-address listing connected by a
   pasted review link legitimately has NO rating and NO review count -- Google
   returns neither. This component used to be typed `rating: number` and called
   `rating.toFixed(1)` unguarded, which threw
   "Cannot read properties of null (reading 'toFixed')" and blanked the whole
   onboarding screen at the exact moment an SAB owner connected. Verified live
   against the real Own The Box Marketing listing, 2026-07-10.

   A listing with no reviews yet is a normal state, not an error. Say so plainly
   rather than crashing or fabricating a score. */
function RatingRow({
  rating,
  count,
}: {
  rating?: number | null;
  count?: number | null;
}) {
  const hasRating = typeof rating === 'number' && Number.isFinite(rating);
  const reviews = typeof count === 'number' && Number.isFinite(count) ? count : 0;

  if (!hasRating) {
    return (
      <div className="cbz2-rating">
        <span className="cbz2-rating__count">
          {reviews > 0
            ? `${reviews.toLocaleString()} review${reviews === 1 ? '' : 's'}`
            : 'No reviews yet'}
        </span>
      </div>
    );
  }

  return (
    <div className="cbz2-rating">
      <span className="cbz2-star" aria-hidden="true">
        <svg viewBox="0 0 20 20" width="18" height="18" fill="var(--gold)">
          <path d="M10 1.6l2.5 5.1 5.6.8-4.1 4 1 5.6L10 14.5 4.9 17l1-5.6-4.1-4 5.6-.8z" />
        </svg>
      </span>
      <b>{rating.toFixed(1)}</b>
      <span className="cbz2-rating__count">
        {reviews.toLocaleString()} review{reviews === 1 ? '' : 's'}
      </span>
    </div>
  );
}

/* A self-contained static map thumbnail with a pin at the listing coordinates,
   visible without scrolling. No external tile host / no map key needed (pre-
   revenue + CSP-safe); the coordinate caption keeps it honest -- it shows the
   real lat/lng, it does not fabricate street names. */
function MapThumb({ lat, lng }: { lat: number; lng: number }) {
  return (
    <div className="cbz2-map" role="img" aria-label={`Map location at ${lat.toFixed(4)}, ${lng.toFixed(4)}`}>
      <svg viewBox="0 0 320 120" preserveAspectRatio="xMidYMid slice" width="100%" height="100%" aria-hidden="true">
        <rect width="320" height="120" fill="var(--bg-3)" />
        <path d="M-10 38 H330" stroke="var(--line)" strokeWidth="7" />
        <path d="M-10 86 H330" stroke="var(--line)" strokeWidth="5" />
        <path d="M96 -10 V130" stroke="var(--line)" strokeWidth="6" />
        <path d="M214 -10 V130" stroke="var(--line)" strokeWidth="4" />
        <path d="M-10 6 L150 130" stroke="var(--line-2)" strokeWidth="3" />
        <g transform="translate(160 44)">
          {/* Soft contact shadow under the pin -- scoped, theme-aware token
              (defined on .cbz2-map). Not a brand color. */}
          <ellipse cx="0" cy="31" rx="9" ry="3.5" fill="var(--cbz2-pin-shadow)" />
          <path d="M0 30 C0 30 -13 12 -13 1 A13 13 0 1 1 13 1 C13 12 0 30 0 30 Z" fill="var(--accent)" />
          {/* Fixed-contrast light center dot: a FUNCTIONAL graphic constant on
              the accent pin (reads as the pin's hole), NOT a brand color -- it
              must stay a light dot in any theme, so it is intentionally literal. */}
          <circle cx="0" cy="0" r="4.6" fill="white" />
        </g>
      </svg>
      <span className="cbz2-map__coords">{lat.toFixed(4)}, {lng.toFixed(4)}</span>
    </div>
  );
}

export interface ConfirmBusinessCardProps {
  /* Called with the canonical connected result of the confirm action. The
     real send gate reads google_connection_status === 'connected', not this
     callback; it exists so a parent can react (e.g. optimistic UI). */
  onConfirmed?: (confirmed: boolean) => void;
  /* Called when the owner chooses "Skip for now" so onboarding can continue
     without a connected business (they can finish later; sending stays
     gated until the listing is confirmed). */
  onSkip?: () => void;
  /* DEV-ONLY visual-preview seed (mirrors InlineCardForm's `previewNoop`).
     When set, the card mounts straight into its reflect-back success state
     with this sample result so the /dev/confirm-business route can screenshot
     spec 3.4 without a backend/confirm round-trip. Guarded by import.meta.env.DEV
     so a production build can never enter this path; the real card never passes
     it, so live behavior (hooks, gates, confirm logic) is unchanged. */
  previewConfirmResult?: ConfirmListingResult;
}

export function ConfirmBusinessCard({ onConfirmed, onSkip, previewConfirmResult }: ConfirmBusinessCardProps) {
  const business = useBusiness();
  const confirmListing = useConfirmListing();

  const profile = business.data?.profile;
  const connection = business.data?.google_connection ?? null;
  const candidate = business.data?.google_candidate ?? null;
  const status = connection?.google_connection_status ?? 'not_started';
  const isConnected = status === 'connected';

  // ---- UI state ----
  const [manual, setManual] = useState(false); // owner chose the manual-connect screen
  const [confirmResult, setConfirmResult] = useState<ConfirmListingResult | null>(
    import.meta.env.DEV ? (previewConfirmResult ?? null) : null,
  );
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [validationFail, setValidationFail] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [skipped, setSkipped] = useState(false);

  // ---- Places fallback search state ----
  // FIX 2: ONE Google-style input. The owner types name + city/state in a single
  // box ("Own The Box Marketing LLC, Saunderstown RI"); the whole string is the
  // NOTE (2026-07-16, Steve's directive): the Places TEXT-SEARCH box is REMOVED
  // from this card entirely -- not hidden. Places text search returns
  // ZERO_RESULTS for a service-area business (proven repeatedly, permanent), so
  // a type-your-business-name search can never find our ICP and only dead-ends
  // owners. The connect paths are now: the authoritative GBP-managed-locations
  // picker (below) + the pasted review link. No search box anywhere.

  // ---- GBP-managed-locations picker (the PRIMARY find-my-business path) ----
  // Google Places text search returns ZERO_RESULTS for a service-area business,
  // so the old name-search could never find them. A listing the owner's Google
  // account MANAGES is returned directly by the now-approved GBP API -- address
  // or not -- so we let them pick theirs from that authoritative list.
  const [gbpLocations, setGbpLocations] = useState<GbpManagedLocation[] | null>(null);
  const [gbpConfirmingId, setGbpConfirmingId] = useState<string | null>(null);
  const [gbpError, setGbpError] = useState<string | null>(null);

  // ---- Paste-your-review-link state (the SAB / hidden-listing path) ----
  const queryClient = useQueryClient();
  const [pasteUrl, setPasteUrl] = useState('');
  const [pasting, setPasting] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [pasteResult, setPasteResult] = useState<ConnectReviewLinkResult | null>(null);
  const pasteParsed = parseReviewLink(pasteUrl);

  const confirming = confirmingId !== null;

  // Fetch the owner's GBP-managed locations once they've connected Google (a
  // token exists) but aren't fully connected yet. This is the authoritative
  // "which of your businesses is this?" list that WORKS for service-area
  // businesses Places text search can't find. Best-effort: any failure just
  // leaves the paste-link + Places search fallbacks in place.
  const hasToken = status !== 'not_started';
  useEffect(() => {
    if (!hasToken || isConnected || gbpLocations !== null) return;
    const ctrl = new AbortController();
    api
      .listGbpLocations(ctrl.signal)
      .then((res) => setGbpLocations(res.locations ?? []))
      .catch(() => {
        // Aborts + transient errors: fall back to an empty list (paste/search
        // stay available). Do not surface an error for a background prefetch.
        if (!ctrl.signal.aborted) setGbpLocations([]);
      });
    return () => ctrl.abort();
  }, [hasToken, isConnected, gbpLocations]);

  /* Confirm the location the owner picked from their GBP-managed list. GBP
     ownership IS the confirmation, so this is not gated behind Places returning
     reviews -- it connects a service-area business immediately. */
  async function confirmGbp(loc: GbpManagedLocation) {
    setGbpError(null);
    setConfirmError(null);
    setValidationFail(null);
    setGbpConfirmingId(loc.location_id);
    try {
      const res = await api.confirmGbpLocation(loc.location_id);
      // Reuse the shared reflect-back success screen (same listing shape).
      setConfirmResult({
        ok: res.ok,
        confirmed_place_id: res.confirmed_place_id ?? '',
        confirmed_at: res.confirmed_at ?? '',
        google_connection_status: res.google_connection_status,
        reviews_imported: res.reviews_imported,
        listing: res.listing,
        import: res.import,
      });
      setManual(false);
      onConfirmed?.(true);
      void queryClient.invalidateQueries();
    } catch (e) {
      onConfirmed?.(false);
      setGbpError(
        apiErrorMessage(
          e,
          "We couldn't connect that business just now. Please try again in a moment.",
        ),
      );
    } finally {
      setGbpConfirmingId(null);
    }
  }

  // ---- Disconnect: THE WAY OUT (2026-07-10) ----
  // A stored confirmed_review_url makes the backend report CONNECTED
  // unconditionally, and nothing else clears it. Before this, an owner who
  // connected the wrong Google listing was pinned to it forever, with every
  // review request pointing at the wrong business. Clears the connection only;
  // the business profile stays put and stays editable in Settings.
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  async function handleDisconnect() {
    setDisconnecting(true);
    setDisconnectError(null);
    try {
      await api.disconnect();
      // Drop every local echo of the old connection before refetching, so no
      // stale listing can flash back onto the screen.
      setPasteResult(null);
      setConfirmResult(null);
      setPasteUrl('');
      setConfirmDisconnect(false);
      setManual(false);
      onConfirmed?.(false);
      await queryClient.invalidateQueries();
    } catch (err) {
      setDisconnectError(
        err instanceof Error
          ? err.message
          : "We couldn't disconnect that listing. Try again in a moment.",
      );
    } finally {
      setDisconnecting(false);
    }
  }

  /* Rendered in EVERY connected state. There are three of them (fresh confirm,
     fresh pasted-link connect, and already-connected-on-load); an owner stuck on
     the wrong listing can land in any of them, so the exit has to exist in all
     three. Defined once here rather than copied three times. */
  const disconnectAction = (
    <div className="cbz2-actions" style={{ marginTop: 6 }}>
      {!confirmDisconnect ? (
        <button
          className="btn btn--text"
          onClick={() => setConfirmDisconnect(true)}
          disabled={disconnecting}
        >
          Disconnect this Google listing
        </button>
      ) : (
        <div>
          <p className="react-note">
            This disconnects the listing so you can connect a different one. Your
            business details are kept, and nothing is sent to your customers.
          </p>
          <button
            className="btn btn--primary"
            onClick={() => void handleDisconnect()}
            disabled={disconnecting}
          >
            {disconnecting ? 'Disconnecting...' : 'Yes, disconnect it'}
          </button>
          <button
            className="btn btn--text"
            onClick={() => setConfirmDisconnect(false)}
            disabled={disconnecting}
          >
            Keep it connected
          </button>
        </div>
      )}
      {disconnectError && (
        <p className="react-note react-note--err" role="alert">
          {disconnectError}
        </p>
      )}
    </div>
  );

  /* The GBP-managed-locations picker, defined ONCE and rendered in BOTH the
     candidate screen and the manual/fallback screen. This is the AUTHORITATIVE
     find-my-business path: the businesses the owner's Google account manages,
     returned directly by the (now-approved) GBP API -- so it finds a service-
     area business that Google Places text search + autocomplete BOTH return
     zero for (verified empirically 2026-07-14: neither Places-New Text Search
     nor Autocomplete surfaces the OTB SAB listing). Previously it lived only on
     the manual fallback, so an owner who saw a single auto-candidate
     first (often the WRONG listing) had to click "this isn't my business"
     before their real managed listings appeared. Surfacing it on the candidate
     screen too makes the authoritative pick obvious. Null when the account
     manages no locations (or GBP is still ungated) -> paste-link + Places search
     remain the fallbacks. */
  const gbpPicker =
    gbpLocations && gbpLocations.length > 0 ? (
      <div className="cbz-gbp">
        <div className="cbz-results__head">Which of these is your business?</div>
        <p className="cbz-gbp__hint">
          These are the businesses on your Google account. Pick yours to connect
          it. This works even for a service-area business with no public address.
        </p>
        <div className="place-pick__results">
          {gbpLocations.map((loc) => {
            const busy = gbpConfirmingId === loc.location_id;
            return (
              <button
                key={loc.location_id}
                className="place-pick__item"
                onClick={() => void confirmGbp(loc)}
                disabled={gbpConfirmingId !== null}
              >
                <span className="place-pick__name">{loc.name || 'Your business'}</span>
                <span className="place-pick__addr">
                  {busy
                    ? 'Connecting...'
                    : loc.address || 'Service-area business (no public address)'}
                </span>
              </button>
            );
          })}
        </div>
        {gbpError && <div className="cbz2-fail" role="alert">{gbpError}</div>}
      </div>
    ) : null;

  /* Connect by the owner's OWN pasted Google review link / Maps URL / place_id.
     THE path for a service-area / hidden-address listing that Google Places
     search cannot find. The pasted destination is stored + used as the review
     link; a place_id is resolved best-effort for import + reflect-back. */
  async function connectByPaste() {
    setPasteError(null);
    const parsed = parseReviewLink(pasteUrl);
    if (!parsed.valid) {
      setPasteError(
        'That does not look like a Google review link. Paste your Google review link, Google Maps listing URL, or place ID.',
      );
      return;
    }
    setPasting(true);
    try {
      const res = await api.connectReviewLink(parsed.raw);
      if (res.ok) {
        setPasteResult(res);
        setManual(false);
        onConfirmed?.(true);
        // Refresh the canonical connection state so every surface flips to
        // connected without a reload.
        void queryClient.invalidateQueries();
      } else {
        setPasteError('We could not connect that link just now. Please try again.');
      }
    } catch (e) {
      onConfirmed?.(false);
      if (e instanceof ApiError && e.status === 400) {
        setPasteError(
          'That does not look like a valid Google review link. Double-check it and paste it again.',
        );
      } else {
        // Honest, status-aware message: a 401 that reaches here means the
        // Supabase session is gone (recovery already failed + we are signing
        // out) -> "session expired, sign in again", NOT "try again in a moment".
        setPasteError(
          apiErrorMessage(
            e,
            'We could not connect that link just now. Please try again in a moment.',
          ),
        );
      }
    } finally {
      setPasting(false);
    }
  }

  /* The one write path: explicitly affirm a place. The backend validates
     before saving, so a wrong pick 422s and NOTHING is stored. */
  async function confirmPlace(placeId: string) {
    setValidationFail(null);
    setConfirmError(null);
    setConfirmingId(placeId);
    try {
      const res = await confirmListing.confirm(placeId);
      setConfirmResult(res);
      setManual(false);
      onConfirmed?.(true);
    } catch (e) {
      onConfirmed?.(false);
      if (e instanceof ApiError && e.status === 422) {
        setValidationFail(
          "We couldn't verify that listing. It either doesn't resolve on Google or has no reviews yet, so we did NOT save it. Pick your business from your Google account list, or paste your review link instead.",
        );
      } else if (e instanceof ApiError && e.status === 503) {
        setConfirmError('We could not reach Google just now. Please try again in a moment.');
      } else {
        // Status-aware: distinguishes an expired session (sign in again) from a
        // server hiccup (retry), instead of a blanket "try again in a moment".
        setConfirmError(
          apiErrorMessage(
            e,
            'We could not confirm that listing just now. Please try again in a moment.',
          ),
        );
      }
    } finally {
      setConfirmingId(null);
    }
  }

  /* Drop back to the manual-connect screen (GBP managed-listings picker +
     paste-your-review-link). No search box lives there anymore. */
  function startManualConnect() {
    setManual(true);
    setConfirmResult(null);
    setValidationFail(null);
    setConfirmError(null);
  }

  const skipBtn = (
    <button
      className="btn btn--text cbz2-skip"
      onClick={() => {
        setSkipped(true);
        onSkip?.();
      }}
    >
      Skip for now, connect Google later
    </button>
  );

  /* Reflect-back body shared by the in-session success and the return-visit
     connected state (spec 3.4: rating + newest review from the CONFIRMED
     listing, so a wrong match is caught at a glance). */
  function reflectBack(listing: ConfirmedListing, reviewsImported: number) {
    return (
      <>
        <div className="cbz2-success__badge">
          <CheckIcon size={20} />
        </div>
        <div className="confirm-biz__title">You're connected to Google</div>
        <p className="confirm-biz__sub">
          Here's what we pulled from your confirmed listing. If these reviews
          aren't yours, this is the wrong business, so tell us now.
        </p>

        <div className="cbz2-name">{listing.name}</div>
        <div className="cbz2-addr">{listing.address}</div>
        {listing.lat != null && listing.lng != null && (
          <MapThumb lat={listing.lat} lng={listing.lng} />
        )}
        <RatingRow rating={listing.rating} count={listing.review_count} />
        {listing.newest_review_snippet && (
          <blockquote className="cbz2-snippet">
            <span className="cbz2-snippet__lab">Your most recent review</span>
            {listing.newest_review_snippet}
          </blockquote>
        )}

        <p className="react-note" style={{ marginTop: 14 }}>
          {reviewsImported > 0
            ? `${reviewsImported.toLocaleString()} review${reviewsImported === 1 ? '' : 's'} imported. Your review link is ready to send.`
            : 'Your review link is ready to send.'}
        </p>

        <div className="cbz2-actions">
          <button className="btn btn--text" onClick={startManualConnect}>
            This isn't my business
          </button>
        </div>

        {disconnectAction}
      </>
    );
  }

  // ---------- SKIPPED ----------
  if (skipped && !isConnected) {
    return (
      <div className="confirm-biz">
        <div className="confirm-biz__title">Connect Google later</div>
        <p className="confirm-biz__sub">
          No problem. You can finish connecting Google any time from Settings.
          You'll need it before your first review request goes out, but you can
          keep setting up now.
        </p>
        <button className="btn btn--ghost" onClick={() => setSkipped(false)}>
          Actually, let me finish now
        </button>
      </div>
    );
  }

  // ---------- SUCCESS / reflect-back (in-session confirm just happened) ----------
  if (confirmResult) {
    return <div className="confirm-biz cbz2-success">{reflectBack(confirmResult.listing, confirmResult.reviews_imported)}</div>;
  }

  // ---------- SUCCESS via PASTED review link (SAB / hidden-listing path) ----------
  if (pasteResult) {
    return (
      <div className="confirm-biz cbz2-success">
        <div className="cbz2-success__badge">
          <CheckIcon size={20} />
        </div>
        <div className="confirm-biz__title">You're connected to Google</div>
        {pasteResult.listing ? (
          <>
            <p className="confirm-biz__sub">
              Here's what we pulled from your listing. If this isn't your business,
              tell us now.
            </p>
            <div className="cbz2-name">{pasteResult.listing.name}</div>
            <div className="cbz2-addr">{pasteResult.listing.address}</div>
            {pasteResult.listing.lat != null && pasteResult.listing.lng != null && (
              <MapThumb lat={pasteResult.listing.lat} lng={pasteResult.listing.lng} />
            )}
            <RatingRow
              rating={pasteResult.listing.rating}
              count={pasteResult.listing.review_count}
            />
          </>
        ) : (
          <p className="confirm-biz__sub">
            Your review link is saved and ready. Customers who get a request will
            land on your Google review page. (Your existing reviews import
            automatically once Google approves our listings access.)
          </p>
        )}
        <p className="react-note" style={{ marginTop: 14, wordBreak: 'break-all' }}>
          Review link: {pasteResult.review_url}
        </p>
        <div className="cbz2-actions">
          <button
            className="btn btn--text"
            onClick={() => {
              setPasteResult(null);
              startManualConnect();
            }}
          >
            This isn't my business
          </button>
        </div>
        {disconnectAction}
      </div>
    );
  }

  // ---------- LOADING ----------
  if (business.isLoading && !business.data) {
    return (
      <div className="confirm-biz">
        <div className="confirm-biz__title">Checking your Google listing...</div>
        <p className="confirm-biz__sub">One moment while we pull the business connected to your Google account.</p>
        <div className="cbz2-skeleton" aria-hidden="true">
          <div className="cbz2-sk cbz2-sk--line" />
          <div className="cbz2-sk cbz2-sk--line short" />
          <div className="cbz2-sk cbz2-sk--map" />
          <div className="cbz2-sk cbz2-sk--btn" />
        </div>
      </div>
    );
  }

  // ---------- ALREADY CONNECTED on load (no in-session result) ----------
  if (isConnected && !manual) {
    const name = (profile?.business_name ?? '').trim();
    const address = (profile?.business_address ?? '').trim();
    const reviewsImported = connection?.reviews_imported ?? 0;
    return (
      <div className="confirm-biz cbz2-success">
        <div className="cbz2-success__badge">
          <CheckIcon size={20} />
        </div>
        <div className="confirm-biz__title">You're connected to Google</div>
        <p className="confirm-biz__sub">
          Your business is confirmed and your review link is ready. If this
          isn't the right business, you can pick the correct one.
        </p>
        {name && <div className="cbz2-name">{name}</div>}
        {address && <div className="cbz2-addr">{address}</div>}
        <p className="react-note" style={{ marginTop: 14 }}>
          {reviewsImported > 0
            ? `${reviewsImported.toLocaleString()} review${reviewsImported === 1 ? '' : 's'} imported from Google.`
            : 'Your review link is ready to send.'}
        </p>
        <div className="cbz2-actions">
          <button className="btn btn--text" onClick={startManualConnect}>
            This isn't my business
          </button>
        </div>
        {disconnectAction}
      </div>
    );
  }

  // ---------- CANDIDATE FOUND (default; the authenticated listing) ----------
  if (candidate && !manual) {
    const isThis = confirmingId === candidate.place_id;
    return (
      <div className="confirm-biz cbz2">
        <div className="confirm-biz__title">Is this your business?</div>
        <p className="confirm-biz__sub">
          This is the business on your Google account. Check it carefully. Your
          review requests and your review link will point here.
        </p>

        <div className="cbz2-card">
          {/* Headline = business name (largest). */}
          <div className="cbz2-name">{candidate.name}</div>
          {/* Full street address -- the primary disambiguator, never truncated. */}
          <div className="cbz2-addr cbz2-addr--primary">{candidate.address}</div>

          {/* Guarded like the other two call sites: a service-area listing has
              no coordinates, and MapThumb calls lat.toFixed(4). */}
          {candidate.lat != null && candidate.lng != null && (
            <MapThumb lat={candidate.lat} lng={candidate.lng} />
          )}

          <RatingRow rating={candidate.rating} count={candidate.review_count} />

          {candidate.newest_review_snippet && (
            <blockquote className="cbz2-snippet">
              <span className="cbz2-snippet__lab">Most recent review</span>
              {candidate.newest_review_snippet}
            </blockquote>
          )}

          {candidate.category && <div className="cbz2-cat">{candidate.category}</div>}
        </div>

        {validationFail && <div className="cbz2-fail" role="alert">{validationFail}</div>}
        {confirmError && <div className="react-note" role="alert">{confirmError}</div>}

        <div className="cbz2-confirm">
          <button
            className="btn btn--primary cbz2-yes"
            onClick={() => void confirmPlace(candidate.place_id)}
            disabled={confirming}
          >
            {isThis ? 'Confirming...' : 'Yes, this is my business'}
          </button>
          <button className="btn btn--ghost cbz2-no" onClick={startManualConnect} disabled={confirming}>
            This isn't my business
          </button>
        </div>

        {/* Authoritative managed-listings picker, surfaced right here so an owner
            whose auto-candidate is the WRONG listing can pick their real business
            in one tap -- without first having to click "this isn't my business".
            Only renders when the account actually manages locations. */}
        {gbpPicker && (
          <>
            <div className="cbz-or">or pick the business you manage on Google</div>
            {gbpPicker}
          </>
        )}
        {skipBtn}
      </div>
    );
  }

  // ---------- MANUAL CONNECT (NO SEARCH -- removed 2026-07-16, Steve) ----------
  // Two honest paths only: the authoritative GBP managed-listings picker (works
  // for service-area businesses) + the pasted review link. The Places name-search
  // box is deleted, not hidden: it returns ZERO_RESULTS for our ICP, permanently.
  return (
    <div className="confirm-biz cbz2">
      <div className="confirm-biz__title">Connect your business</div>
      <p className="confirm-biz__sub">
        Pick the business on your Google account, or paste your Google review
        link and we&apos;ll connect it directly.
      </p>

      {/* PRIMARY: the businesses the owner's connected Google account MANAGES
          (returned by the GBP API -- finds service-area listings). */}
      {gbpPicker && (
        <>
          {gbpPicker}
          <div className="cbz-or">or connect another way</div>
        </>
      )}

      {/* The pasted-review-link path (2026-07-09). First-class, not buried. */}
      <div className="cbz-paste">
        <div className="cbz-paste__lab">Paste your Google review link</div>
        <p className="cbz-paste__hint">
          Paste your own Google review link or Maps listing URL and we&apos;ll
          connect it directly. Works for every business, including service-area
          businesses with no public address.
        </p>
        <label className="cbz-field">
          <span className="cbz-field__lab">Your Google review link or Maps URL</span>
          <input
            className="inp"
            value={pasteUrl}
            placeholder="e.g. https://g.page/r/XXXX/review"
            onChange={(e) => { setPasteUrl(e.target.value); setPasteError(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && pasteParsed.valid) void connectByPaste();
            }}
            autoComplete="off"
            inputMode="url"
          />
        </label>
        {pasteError && <div className="cbz2-fail" role="alert">{pasteError}</div>}
        <button
          className="btn btn--primary cbz-paste__btn"
          onClick={() => void connectByPaste()}
          disabled={pasting || !pasteParsed.valid}
          style={!pasteParsed.valid || pasting ? { opacity: 0.6 } : undefined}
        >
          {pasting ? 'Connecting...' : 'Connect this link'}
        </button>
        <div className="cbz-paste__where">
          Where to find it: on your Google Business Profile tap "Ask for
          reviews" / "Share review form" to copy your review link.
        </div>
      </div>

      {validationFail && <div className="cbz2-fail" role="alert">{validationFail}</div>}
      {confirmError && <div className="react-note" role="alert">{confirmError}</div>}

      {/* If a candidate exists, let the owner go back to it from manual mode. */}
      {candidate && (
        <button className="btn btn--text" onClick={() => setManual(false)}>
          Back to the business on my Google account
        </button>
      )}
      {skipBtn}
    </div>
  );
}

export default ConfirmBusinessCard;
